import React, { useMemo, useState } from 'react';
import { Target, Scale, Flame, Home, Megaphone, Crown, Shield } from 'lucide-react';
import FormationCard from './FormationCard';
import PlayingCardLogo from './PlayingCardLogo';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import BearHuntClock from './BearHuntClock';
import PresetProfiles from './PresetProfiles';
import { SuitFor, typeMeta } from './Suit';
import { Slider } from './ui/slider';
import { optimize, MAX_FORMATIONS, TIERS } from '../lib/optimizer';
import { useT } from '../i18n/LanguageContext';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US');

function renderBanner(str, { finalN, cav, arch, inf }) {
  const parts = str.split(/(§N§|§CAV§|§ARCH§|§INF§)/);
  return parts.map((p, i) => {
    if (p === '§N§')   return <b key={i} className="text-[#ff6b6b]">{finalN}</b>;
    if (p === '§CAV§') return <b key={i} className="text-[#ff6b6b]">{cav}</b>;
    if (p === '§ARCH§') return <b key={i} className="text-[#ff6b6b]">{arch}</b>;
    if (p === '§INF§') return <b key={i} className="text-[#ff6b6b]">{inf}</b>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

const emptyTiers = () => TIERS.reduce((o, t) => { o[t] = ''; return o; }, {});

const initialTroops = {
  infantry: emptyTiers(),
  cavalry:  emptyTiers(),
  archers:  emptyTiers(),
};

export default function TroopOptimizer() {
  const { t } = useT();
  const [limit, setLimit] = useState(80000);
  const [troops, setTroops] = useState(initialTroops);
  const [mode, setMode] = useState('balanced');
  const [infPer, setInfPer] = useState(5000);
  const [result, setResult] = useState(null);

  const setTroop = (type, tier, val) => {
    const clean = val.replace(/[^0-9]/g, '');
    setTroops((prev) => ({ ...prev, [type]: { ...prev[type], [tier]: clean } }));
  };

  const handleDeal = () => {
    const r = optimize({ troops, limit, mode, infPer });
    setResult(r);
    setTimeout(() => {
      const el = document.getElementById('results-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const totalInput = useMemo(() => {
    const sum = (o) => Object.values(o).reduce((a, b) => a + (parseInt(b) || 0), 0);
    return sum(troops.infantry) + sum(troops.cavalry) + sum(troops.archers);
  }, [troops]);

  const tiers = TIERS;
  const types = ['infantry', 'cavalry', 'archers'];

  return (
    <div className="min-h-screen pb-16">
      {/* HEADER */}
      <header className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 60%, #141414 100%)' }}>
        {/* subtle diamond grid */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, #c92a2a 0 1px, transparent 1px 18px), repeating-linear-gradient(-45deg, #c92a2a 0 1px, transparent 1px 18px)',
          }}
        />
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 pt-10 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#c92a2a]/50 bg-[#c92a2a]/10 text-[#ff6b6b] text-xs font-semibold tracking-widest uppercase">
            <Crown className="w-3.5 h-3.5" /> {t('header.badge')}
          </div>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-black tracking-tight">
            <span className="text-[#e03131]">[HOC]</span>{' '}
            <span className="text-white">{t('header.title')}</span>
          </h1>
          <p className="mt-2 text-neutral-400 italic">{t('header.subtitle')}</p>

          <div className="mt-6">
            <PlayingCardLogo />
          </div>

          <p className="mt-6 font-semibold text-[#ff8787] text-sm sm:text-base">
            {t('header.quote')}
          </p>
        </div>
        <div className="h-[3px] bg-gradient-to-r from-transparent via-[#c92a2a] to-transparent" />
      </header>

      <main className="max-w-xl mx-auto px-5 mt-10 space-y-5">
        {/* Bear Hunt Clock */}
        <BearHuntClock />

        {/* Preset Profiles */}
        <PresetProfiles
          snapshot={() => ({ troops, limit, mode, infPer })}
          onLoad={(data) => {
            if (data.troops) setTroops(data.troops);
            if (data.limit !== undefined) setLimit(data.limit);
            if (data.mode) setMode(data.mode);
            if (data.infPer !== undefined) setInfPer(data.infPer);
          }}
        />

        {/* Rally Settings */}
        <Section title={t('rally.section')}>
          <div className="px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-md inline-flex items-center justify-center bg-neutral-900 text-white">
                <Target className="w-5 h-5" />
              </span>
              <div>
                <div className="font-medium text-neutral-900">{t('rally.limitLabel')}</div>
                <div className="text-[11px] text-neutral-500">{t('rally.limitHint', { n: MAX_FORMATIONS })}</div>
              </div>
            </div>
            <input
              type="text"
              inputMode="numeric"
              value={limit}
              onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-32 text-right px-3 py-2 rounded-md border-2 border-neutral-200 bg-white font-semibold text-neutral-900 focus:outline-none focus:border-[#c92a2a]"
            />
          </div>
          <div className="px-5 pb-4 border-t border-neutral-100 pt-4">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md inline-flex items-center justify-center bg-neutral-900 text-white">
                  <Shield className="w-5 h-5" />
                </span>
                <div>
                  <div className="font-medium text-neutral-900">{t('rally.floorLabel')}</div>
                  <div className="text-[11px] text-neutral-500">{t('rally.floorHint')}</div>
                </div>
              </div>
              <span className="px-3 py-1.5 rounded-md bg-[#c92a2a] text-white font-bold tabular-nums text-sm min-w-[70px] text-center">
                {fmt(infPer)}
              </span>
            </div>
            <Slider
              value={[infPer]}
              min={1000}
              max={20000}
              step={500}
              onValueChange={(v) => setInfPer(v[0])}
              className="hoc-slider"
            />
            <div className="flex items-center justify-between mt-2 text-[11px] font-semibold text-neutral-500">
              <button type="button" onClick={() => setInfPer(1000)}  className="hover:text-[#c92a2a]">1k</button>
              <button type="button" onClick={() => setInfPer(3000)}  className="hover:text-[#c92a2a]">3k</button>
              <button type="button" onClick={() => setInfPer(5000)}  className="hover:text-[#c92a2a]">5k</button>
              <button type="button" onClick={() => setInfPer(7000)}  className="hover:text-[#c92a2a]">7k</button>
              <button type="button" onClick={() => setInfPer(10000)} className="hover:text-[#c92a2a]">10k</button>
              <button type="button" onClick={() => setInfPer(15000)} className="hover:text-[#c92a2a]">15k</button>
            </div>
          </div>
        </Section>

        {/* Troops */}
        <Section
          title={t('troops.section')}
          right={<span className="text-xs text-neutral-500 font-medium">{t('troops.total', { n: fmt(totalInput) })}</span>}
        >
          <div className="px-5 py-4">
            <div className="grid grid-cols-[minmax(90px,1fr)_repeat(5,minmax(0,1fr))] sm:grid-cols-[1fr_repeat(5,70px)] gap-x-1.5 sm:gap-x-2 gap-y-2 items-center">
              <div />
              {tiers.map((t) => (
                <div key={t} className="text-center text-[11px] sm:text-xs font-bold text-neutral-500 tracking-wider">{t}</div>
              ))}
              {types.map((type) => {
                const meta = typeMeta[type];
                return (
                  <React.Fragment key={type}>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span className={`w-7 h-7 inline-flex items-center justify-center rounded-md flex-shrink-0 ${meta.iconClass}`}>
                        <SuitFor name={meta.suit} size={14} />
                      </span>
                      <span className="font-medium text-neutral-900 text-sm sm:text-base">{t(`troops.${type}`)}</span>
                    </div>
                    {tiers.map((tier) => (
                      <input
                        key={tier}
                        type="text"
                        inputMode="numeric"
                        value={troops[type][tier]}
                        onChange={(e) => setTroop(type, tier, e.target.value)}
                        placeholder="0"
                        className="min-w-0 text-center py-2 px-1 rounded-md border-2 border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400 text-sm focus:outline-none focus:border-[#c92a2a]"
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-neutral-500 italic">
              {t('troops.hint')}
            </p>
          </div>
        </Section>

        {/* Mode */}
        <Section title={t('mode.section')}>
          <div className="px-5 py-4 grid grid-cols-2 gap-3">
            <ModeButton
              active={mode === 'balanced'}
              onClick={() => setMode('balanced')}
              icon={<Scale className="w-4 h-4" />}
              label={t('mode.even')}
              sub={t('mode.evenSub')}
            />
            <ModeButton
              active={mode === 'strong-first'}
              onClick={() => setMode('strong-first')}
              icon={<Flame className="w-4 h-4" />}
              label={t('mode.royal')}
              sub={t('mode.royalSub')}
            />
          </div>
        </Section>

        {/* Deal button */}
        <button
          onClick={handleDeal}
          className="w-full rounded-xl py-4 font-serif text-xl font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
          style={{
            background: 'linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 100%)',
            border: '1.5px solid #c92a2a',
            boxShadow: '0 8px 24px rgba(201,42,42,0.25), inset 0 0 0 1px rgba(255,255,255,0.04)',
          }}
        >
          {t('deal.button')}
          <span className="ml-2 text-[#ff6b6b]">♥ ♦</span>
          <span className="ml-1">♠ ♣</span>
        </button>

        {/* Results */}
        <div id="results-section">
          {result && <Results result={result} />}
        </div>
      </main>

      <footer className="mt-14 text-center px-6">
        <div className="inline-flex flex-col items-center gap-1.5 px-5 py-4 rounded-xl bg-neutral-900 text-neutral-300 border border-neutral-800">
          <div className="text-xs tracking-widest uppercase text-neutral-500">{t('footer.craftedBy')}</div>
          <div className="font-serif text-lg font-bold text-white flex items-center gap-2">
            <span className="text-[#e03131]">♥</span>
            VelvetClub
            <span className="text-[#e03131]">♦</span>
          </div>
          <div className="text-xs text-neutral-500">{t('footer.attribution')}</div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <section className="rounded-xl bg-white border border-neutral-200 shadow-[0_6px_18px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-[#c92a2a] via-[#e03131] to-[#c92a2a]" />
      <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50/60 flex items-center justify-between">
        <h2 className="font-serif font-bold text-neutral-900">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function ModeButton({ active, onClick, icon, label, sub }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-3 py-3 text-center border-2 transition-colors ${
        active
          ? 'border-[#c92a2a] bg-[#fff5f5]'
          : 'border-neutral-200 bg-white hover:bg-neutral-50'
      }`}
    >
      <div className={`flex items-center justify-center gap-2 font-bold ${active ? 'text-[#c92a2a]' : 'text-neutral-900'}`}>
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-[11px] text-neutral-500 mt-1">{sub}</div>
    </button>
  );
}

function Results({ result }) {
  const { t } = useT();
  const { formations, finalN, limit, leftover, mode, totals } = result;

  const anyLeftover = leftover.totalInf + leftover.totalCav + leftover.totalArch > 0;

  if (formations.length === 0 && !anyLeftover) {
    return (
      <div className="rounded-xl bg-neutral-50 border-2 border-neutral-200 px-5 py-4 text-center text-neutral-600 font-medium">
        {t('deal.noTroops')}
      </div>
    );
  }

  const modeLabel = mode === 'balanced' ? t('mode.even') : t('mode.royal');
  const ModeIcon = mode === 'balanced' ? Scale : Flame;

  return (
    <div className="space-y-4">
      {/* Explanation banner */}
      <div className="rounded-xl text-white px-5 py-4 shadow-md relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 100%)', border: '1px solid #2a2a2a' }}>
        <div className="h-1 bg-gradient-to-r from-transparent via-[#c92a2a] to-transparent absolute top-0 left-0 right-0" />
        {finalN > 0 ? (
          <p className="leading-relaxed text-sm sm:text-base">
            {renderBanner(
              t('deal.bannerWith', {
                n: '§N§',
                plural: finalN === 1 ? '' : 'es',
                max: MAX_FORMATIONS,
                cav: '§CAV§',
                arch: '§ARCH§',
                inf: '§INF§',
              }),
              { finalN, cav: fmt(totals.cavUsed), arch: fmt(totals.archUsed), inf: fmt(result.infPer) }
            )}
            {' '}
            <span className="inline-flex items-center gap-1 text-[#ff6b6b] font-bold">
              <ModeIcon className="w-4 h-4" />{modeLabel}
            </span>
          </p>
        ) : (
          <p className="text-sm">{t('deal.bannerEmpty')}</p>
        )}
      </div>

      {/* Formation cards */}
      {formations.map((f) => (
        <FormationCard key={f.index} formation={f} limit={limit} />
      ))}

      {/* Leftover */}
      {anyLeftover && <LeftoverCard leftover={leftover} />}
    </div>
  );
}

function LeftoverCard({ leftover }) {
  const { t } = useT();
  const { infantry, cavalry, archers, totalInf, totalCav, totalArch } = leftover;
  const tierClasses = {
    L10: 'bg-red-100 text-red-900 ring-1 ring-red-300',
    L9:  'bg-neutral-200 text-neutral-800 ring-1 ring-neutral-400',
    L8:  'bg-neutral-100 text-neutral-700 ring-1 ring-neutral-300',
    L7:  'bg-amber-100 text-amber-800 ring-1 ring-amber-300',
    L6:  'bg-stone-100 text-stone-700 ring-1 ring-stone-300',
  };
  const rows = [];
  const push = (type, pool) => {
    for (const tier of TIERS) {
      if ((pool[tier] || 0) > 0) rows.push({ type, tier, count: pool[tier] });
    }
  };
  push('infantry', infantry);
  push('cavalry', cavalry);
  push('archers', archers);
  const grand = totalInf + totalCav + totalArch;

  return (
    <div className="relative rounded-xl bg-white border border-neutral-200 shadow-[0_6px_18px_rgba(0,0,0,0.06)] overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-[#c92a2a] via-[#e03131] to-[#c92a2a]" />
      <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 bg-neutral-50/60">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#0d0d0d] text-white">
            <Home className="w-4 h-4" />
          </span>
          <h3 className="font-serif text-lg font-bold text-neutral-900">{t('leftover.title')}</h3>
        </div>
        <span className="px-3 py-1 rounded-full bg-neutral-200 text-neutral-700 text-xs font-semibold">{t('leftover.chip', { n: grand.toLocaleString() })}</span>
      </div>

      <div className="px-5 pt-3 pb-2">
        <p className="text-sm text-neutral-700 italic">{t('leftover.intro')}</p>
      </div>

      {/* Two fun options */}
      <div className="px-5 pb-3 grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#0d0d0d] text-white">
              <SuitFor name="spade" size={13} />
            </span>
            <h4 className="font-serif font-bold text-neutral-900">{t('leftover.optionAHead')}</h4>
          </div>
          <p className="text-[13px] text-neutral-700 leading-relaxed">{t('leftover.optionADesc')}</p>
          <div className="mt-2 text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">{t('leftover.optionATag')}</div>
        </div>

        <div className="rounded-lg border border-[#c92a2a]/40 bg-[#fff5f5] p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#c92a2a] text-white">
              <Megaphone className="w-3.5 h-3.5" />
            </span>
            <h4 className="font-serif font-bold text-neutral-900">{t('leftover.optionBHead')}</h4>
          </div>
          <p className="text-[13px] text-neutral-700 leading-relaxed">{t('leftover.optionBDesc')}</p>
          <div className="mt-2 text-[11px] font-semibold text-[#c92a2a] uppercase tracking-wider">{t('leftover.optionBTag')}</div>
        </div>
      </div>

      <div className="px-5 pb-4">
        <div className="rounded-lg bg-neutral-50 border border-neutral-200">
          <div className="px-4 py-2 border-b border-neutral-200 text-xs font-bold text-neutral-500 uppercase tracking-wider">
            {t('leftover.bench')}
          </div>
          <ul className="divide-y divide-dashed divide-neutral-200 px-4">
            {rows.map((it, idx) => (
              <li key={idx} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 inline-flex items-center justify-center rounded-md ${typeMeta[it.type].iconClass}`}>
                    <SuitFor name={typeMeta[it.type].suit} size={14} />
                  </span>
                  <span className="font-medium text-neutral-900">{t(`troops.${it.type}`)}</span>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${tierClasses[it.tier]}`}>{it.tier}</span>
                </div>
                <span className="font-semibold tabular-nums text-neutral-900">{(it.count || 0).toLocaleString()}</span>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-neutral-200 flex items-center justify-between">
            <span className="font-bold text-neutral-900">{t('formation.total')}</span>
            <span className="font-bold tabular-nums text-neutral-900">{grand.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
